<template>
  <div
    id="app"
    v-loading.fullscreen.lock="globalLoading"
    class="app-container"
    :element-loading-text="loadingText"
    element-loading-background="rgba(0, 0, 0, 0.8)"
    element-loading-spinner="el-icon-loading"
  >
    <!-- 路由视图 -->
    <RouterView v-slot="{ Component }">
      <Transition
        :name="transitionName"
        mode="out-in"
        appear
      >
        <KeepAlive :include="cachedViews">
          <component
            :is="Component"
          />
        </KeepAlive>
      </Transition>
    </RouterView>

    <!-- 全局加载遮罩由 v-loading 指令控制在根容器上 -->

    <!-- 全局消息提示容器 -->
    <div id="message-container"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAppStore } from './stores/app'
import { useUserStore } from './stores/user'
import { useAuth } from '@/composables'

// 应用名称
defineOptions({
  name: 'App'
})

// 路由信息
const route = useRoute()
const router = useRouter()

// 状态管理
const appStore = useAppStore()
const userStore = useUserStore()
const { isLoggedIn } = useAuth()

// 响应式数据
const globalLoading = ref(false)
const loadingText = ref('加载中...')
const transitionName = ref('fade')

// 计算属性
const cachedViews = computed(() => appStore.cachedViews)

/**
 * 设置页面过渡动画
 */
const setTransition = () => {
  // 根据路由层级设置不同的过渡效果
  const toDepth = route.path.split('/').length
  const fromDepth = router.currentRoute.value.path.split('/').length

  if (toDepth > fromDepth) {
    transitionName.value = 'slide-left'
  } else if (toDepth < fromDepth) {
    transitionName.value = 'slide-right'
  } else {
    transitionName.value = 'fade'
  }
}
/**
 * 处理未捕获的Promise错误
 */
const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
  console.error('未捕获的Promise错误:', event.reason)

  // 阻止默认的错误处理
  event.preventDefault()

  // 可以在这里上报错误到监控系统
  // errorReporter.report(event.reason)
}

/**
 * 处理网络状态变化
 */
const handleOnline = () => {
  // 可以在这里重新加载数据或显示提示
}

const handleOffline = () => {
  // 可以在这里显示离线提示
}

/**
 * 监听路由变化
 */
watch(
  () => route.path,
  () => {
    setTransition()

    // 更新页面标题
    const title = route.meta?.title as string
    if (title) {
      import('@/constants/site').then(({ SITE_NAME }) => {
        document.title = `${title} - ${SITE_NAME}`
      })
    }

    // 记录页面访问
    if (isLoggedIn.value) {
      // 可以在这里记录用户行为日志
    }
  },
  { immediate: true }
)

/**
 * 监听应用状态变化
 */
watch(
  () => appStore.loading,
  (loading) => {
    globalLoading.value = loading
  }
)

watch(
  () => appStore.loadingText,
  (text) => {
    loadingText.value = text
  }
)

/**
 * 组件挂载
 */
onMounted(() => {
  // 添加全局事件监听
  window.addEventListener('unhandledrejection', handleUnhandledRejection)
  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)

  // 初始化应用设置
  appStore.initApp()

  // 如果用户已登录，初始化用户数据
  if (isLoggedIn.value) {
    userStore.initializeUser()
  }

})

/**
 * 组件卸载
 */
onUnmounted(() => {
  // 移除全局事件监听
  window.removeEventListener('unhandledrejection', handleUnhandledRejection)
  window.removeEventListener('online', handleOnline)
  window.removeEventListener('offline', handleOffline)

})
</script>

<style lang="scss">
// 全局样式
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body {
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  background-color: #f5f5f5;
}

#app {
  height: 100vh;
  overflow: hidden;
}

.app-container {
  width: 100%;
  height: 100%;
  position: relative;
}

// 页面过渡动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.3s ease;
}

.slide-left-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-left-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s ease;
}

.slide-right-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

// 滚动条样式
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;

  &:hover {
    background: #a8a8a8;
  }
}

// 响应式设计
@media (max-width: 768px) {
  body {
    font-size: 12px;
  }
}

// 打印样式
@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    color: black !important;
  }
}

// 高对比度模式
@media (prefers-contrast: high) {
  body {
    background: white;
    color: black;
  }
}

// 减少动画模式
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
